import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Papa from 'papaparse';

// Custom tooltip for dark theme
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-gray-800 text-gray-100 p-2 border border-gray-700 rounded shadow-lg">
        <p className="font-medium">{`${payload[0].name}: ${payload[0].value}`}</p>
      </div>
    );
  }
  return null;
};

const MessageStatsDashboard = () => {
  const [topAuthors, setTopAuthors] = useState([]);
  const [channelData, setChannelData] = useState([]);
  const [topAuthorsByChannel, setTopAuthorsByChannel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6', '#3B82F6', '#EF4444', '#14B8A6', '#F97316', '#A855F7'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // In a real app, you would use fetch to get the CSV
        // Here we're importing it directly for simplicity
        //const csvFile = require('../data/MessageStats.csv');

        fetch("https://hyonkbot.xyz/auth/stats")
          .then(response => response.text())
          .then(csvData => {
            Papa.parse(csvData, {
              header: true,
              dynamicTyping: true,
              skipEmptyLines: true,
              complete: (results) => {
                // Process top authors
                const authorCounts = {};
                results.data.forEach(row => {
                  if (row.AuthorName) {
                    authorCounts[row.AuthorName] = (authorCounts[row.AuthorName] || 0) + 1;
                  }
                });

                const sortedAuthors = Object.entries(authorCounts)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 10)
                  .map(([name, count]) => ({ name, count }));

                setTopAuthors(sortedAuthors);

                // Process channel data
                const channelCounts = {};
                results.data.forEach(row => {
                  if (row.ChannelName) {
                    channelCounts[row.ChannelName] = (channelCounts[row.ChannelName] || 0) + 1;
                  }
                });

                const channelDataArray = Object.entries(channelCounts)
                  .map(([name, value]) => ({ name, value }))
                  .sort((a, b) => b.value - a.value);

                setChannelData(channelDataArray);

                // Process top authors per channel
                const channelAuthors = {};
                results.data.forEach(row => {
                  if (row.ChannelName && row.AuthorName) {
                    if (!channelAuthors[row.ChannelName]) {
                      channelAuthors[row.ChannelName] = {};
                    }
                    channelAuthors[row.ChannelName][row.AuthorName] =
                      (channelAuthors[row.ChannelName][row.AuthorName] || 0) + 1;
                  }
                });

                const topAuthorsByChannelArray = [];

                Object.entries(channelAuthors).forEach(([channel, authors]) => {
                  const topAuthor = Object.entries(authors)
                    .sort((a, b) => b[1] - a[1])[0];

                  const totalMessages = Object.values(authors).reduce((sum, count) => sum + count, 0);

                  topAuthorsByChannelArray.push({
                    channel,
                    author: topAuthor[0],
                    count: topAuthor[1],
                    percentage: Math.round((topAuthor[1] / totalMessages) * 100)
                  });
                });

                setTopAuthorsByChannel(topAuthorsByChannelArray.sort((a, b) => b.count - a.count));
                setLoading(false);
              },
              error: (error) => {
                setError(`Error parsing CSV: ${error}`);
                setLoading(false);
              }
            });
          })
          .catch(err => {
            setError(`Error fetching CSV: ${err}`);
            setLoading(false);
          });
      } catch (error) {
        setError(`Error: ${error}`);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-gray-200">Loading message statistics...</div>;
  }

  if (error) {
    return <div className="text-red-400 p-4 bg-gray-800 rounded">Error: {error}</div>;
  }

  return (
    <div className="p-6 space-y-8 bg-gray-900 text-gray-100 min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-blue-400">Message Statistics Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Top Authors Chart */}
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-gray-700">
          <h2 className="text-xl font-semibold mb-4 text-gray-200">Top 10 Authors by Message Count</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={topAuthors}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis type="number" stroke="#9CA3AF" />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={100}
                  stroke="#9CA3AF"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  interval={0} // This forces all labels to show
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill="#6366F1" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Channel Distribution Chart */}
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-gray-700">
          <h2 className="text-xl font-semibold mb-4 text-gray-200">Channel Message Distribution</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={channelData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {channelData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top Author per Channel */}
      <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-gray-700">
        <h2 className="text-xl font-semibold mb-4 text-gray-200">Top Author per Channel</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Channel</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Top Author</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Message Count</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">% of Channel</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {topAuthorsByChannel.map((item, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-900'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-200">{item.channel}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{item.author}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{item.count}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{item.percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8 text-center text-gray-400 text-sm">
        <p>Data visualization of HyonkBot statistics • {new Date().toLocaleDateString()}</p>
      </div>
    </div>
  );
};

export default MessageStatsDashboard;